import { Check, RotateCcw, Images } from "lucide-react";
import { getUserDetails, autoDetails, selectedDetailIds, useDetailImage } from "@/lib/details";
import { TYPOLOGIES } from "@/lib/typology";
import { useCatalogues } from "@/context/CatalogueContext";

const Thumb = ({ d, on, auto, onToggle }) => {
  const src = useDetailImage(d);
  return (
    <button data-testid={`detail-pick-${d.detail_id}`} onClick={onToggle}
      className={`relative rounded-md border p-1 text-left transition-colors ${on ? "border-cyan-400 bg-cyan-500/10" : "border-slate-700 hover:border-slate-500"}`}>
      <div className="h-16 bg-white rounded grid place-items-center overflow-hidden">{src ? <img src={src} alt={d.title} className="max-h-16 object-contain" /> : <span className="text-[9px] text-slate-400">loading…</span>}</div>
      <p className="mt-1 text-[10px] leading-tight text-slate-200 truncate">{d.title}</p>
      <p className="text-[9px] text-slate-500 truncate">{d.joint ? `Joint: ${d.joint.replace("+", " ↔ ")}` : `${TYPOLOGIES.find((t) => t.value === d.typology)?.label || d.typology}${d.shutters > 1 ? ` · ${d.shutters}` : ""}`}</p>
      {on && <span className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-cyan-500 grid place-items-center"><Check size={10} className="text-slate-950" /></span>}
      {auto && <span className="absolute top-1.5 left-1.5 px-1 rounded bg-amber-400 text-[8px] font-bold text-slate-950">AUTO</span>}
    </button>
  );
};

export const DetailPicker = ({ item, update }) => {
  useCatalogues();
  const all = getUserDetails();
  const sel = selectedDetailIds(item), auto = autoDetails(item);
  const manual = item.section_details != null;
  const toggle = (id) => update({ ...item, section_details: sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id] });
  return (
    <div className="rounded-md border border-slate-700 p-3 bg-[#0B0F19]/60 space-y-2" data-testid="detail-picker">
      <div className="flex items-center gap-2">
        <Images size={13} className="text-cyan-400" />
        <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400 flex-1">Exact catalogue section details · {sel.length} selected</p>
        {manual && <button data-testid="detail-reset-auto-btn" onClick={() => { const { section_details, ...rest } = item; update(rest); }}
          className="text-[10px] flex items-center gap-1 text-slate-300 hover:text-cyan-300"><RotateCcw size={11} />Auto-match</button>}
      </div>
      {all.length === 0 ? (
        <p className="text-[11px] text-slate-500" data-testid="detail-picker-empty">Upload or import a catalogue (header › Catalogue) to pick the manufacturer's exact section drawings for this combination.</p>
      ) : (
        <>
          <p className="text-[10px] text-slate-500">{manual ? "Manual selection." : "Auto-matched to this item's combination of panel types."} Tap to add or remove.</p>
          <div className="grid grid-cols-3 gap-1.5">
            {all.map((d) => <Thumb key={d.detail_id} d={d} on={sel.includes(d.detail_id)} auto={auto.includes(d.detail_id)} onToggle={() => toggle(d.detail_id)} />)}
          </div>
        </>
      )}
    </div>
  );
};
